import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  TemplateRef,
  ViewChild,
  AfterViewInit, OnDestroy,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { DOCUMENT } from '@angular/common';

@Component({
  selector: 'app-slider',
  templateUrl: './slider.component.html',
  styleUrls: ['./slider.component.scss'],
  standalone: true,
  imports: [NgTemplateOutlet],
})
export class SliderComponent<T> implements AfterViewInit, OnDestroy {
  private readonly document = inject(DOCUMENT);

  @Input({ required: true }) items!: T[];
  @Input({ required: true }) itemTemplate!: TemplateRef<{ $implicit: T }>;
  @Input() itemGapClass = 'gap-6';
  @Input() sliderClass = '';

  @Output() isScrollingChange = new EventEmitter<boolean>();

  @ViewChild('slider') slider: ElementRef<HTMLElement>;

  protected isScrolling = false;
  private isMouseDown = false;
  private startX = 0;
  private scrollLeft = 0;
  private hasDragged = false;

  ngAfterViewInit(): void {
    this.setupListeners();
  }

  private onClick = (e: MouseEvent): void => {
    if (!this.hasDragged) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    this.hasDragged = false;
  };

  private onMouseDown = (e: MouseEvent): void => {
    this.isMouseDown = true;
    this.startX = e.clientX;
    this.scrollLeft = this.slider.nativeElement.scrollLeft;
    this.hasDragged = false;

    e.preventDefault();
  };

  private onMouseMove = (e: MouseEvent): void => {
    if (!this.isMouseDown) return;

    if (!this.isScrolling) {
      this.isScrolling = true;
      this.isScrollingChange.emit(true);
      this.hasDragged = true;
    }

    const sliderEl = this.slider?.nativeElement;
    const walk = e.clientX - this.startX;
    sliderEl.scrollLeft = this.scrollLeft - walk;
  };

  private onMouseUp = (e: MouseEvent): void => {
    if (this.isScrolling) {
      this.isScrolling = false;
      this.isScrollingChange.emit(false);
      e.preventDefault();
    }

    this.isMouseDown = false;
  };

  private setupListeners(): void {
    if (!this.slider) return;

    const sliderEl = this.slider.nativeElement;

    sliderEl.addEventListener('click', this.onClick);
    sliderEl.addEventListener('mousedown', this.onMouseDown, { passive: false });

    this.document.addEventListener('mousemove', this.onMouseMove);
    this.document.addEventListener('mouseup', this.onMouseUp, { passive: false });
  }

  ngOnDestroy(): void {
    const sliderEl = this.slider?.nativeElement;

    if (sliderEl) {
      sliderEl.removeEventListener('click', this.onClick);
      sliderEl.removeEventListener('mousedown', this.onMouseDown);
    }

    this.document.removeEventListener('mousemove', this.onMouseMove);
    this.document.removeEventListener('mouseup', this.onMouseUp);
  }
}
